// The call read on the page (module 04 D288, D299, DESIGN B1895; REQ-2026-09-24-09): the shapes the triage read carries for
// it, and the one click back. Cobrain reads a call placed from a card against that card. A card the call cleared is listed
// above Your list for two days with the line that proves it, and Reopen puts the card back: POST
// /api/triage/call-reads/:judgementId/reopen, the store in the body as every triage POST carries it (routes/triage-read.ts).
// A card the call brought back carries the step agreed on it (`features.call_step`), which the card draws first.
//
// Here and not in `src/api.ts`, which is another track's file, as the call's own client is (call/api.ts).
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated, type PassItem, type Store } from '../api.js';

/**
 * One card a call cleared, as GET /api/triage lists it under `cleared` (routes/triage-read.ts ClearedCard): the person's own,
 * from the last 48 hours, not reopened. An older server sends no `cleared` at all.
 */
export interface ClearedCard {
  judgementId: string;
  /** The pressed card's headline. */
  headline: string | null;
  /** Who was called, and when the call was booked for. */
  callee: string | null;
  callAt: string | null;
  /** What the call did to the card, in Cobrain's words, and the call summary's own words that prove it. */
  line: string | null;
  quote: string | null;
}

/** The step agreed on the call, as the pass writes it on the card (`features.call_step`; rank/types.ts Features). */
export interface CallStep {
  line: string;
  whose: 'you' | 'them' | 'someone_else';
  name: string | null;
  callee: string;
  call_at: string;
  quote: string | null;
  judgement_id: string;
}

const text = (v: unknown): string | null => (typeof v === 'string' ? v : null);

/** The cards a call cleared, from one store's triage read. Absent, or not a list, is none: an older server. */
export function clearedOf(view: unknown): ClearedCard[] {
  const list = (view as { cleared?: unknown } | null | undefined)?.cleared;
  if (!Array.isArray(list)) return [];
  return list.flatMap((c: unknown) => {
    const r = (c ?? {}) as Record<string, unknown>;
    const judgementId = text(r.judgementId);
    if (!judgementId) return [];
    return [{ judgementId, headline: text(r.headline), callee: text(r.callee), callAt: text(r.callAt), line: text(r.line), quote: text(r.quote) }];
  });
}

/** The step a card was brought back with, or null: absent, or missing what its line is built from. */
export function callStepOf(item: Pick<PassItem, 'features'>): CallStep | null {
  const s = (item.features as { call_step?: unknown } | null | undefined)?.call_step as Partial<CallStep> | null | undefined;
  if (!s || typeof s !== 'object') return null;
  if (typeof s.line !== 'string' || !s.line.trim() || typeof s.callee !== 'string' || !s.callee.trim()) return null;
  if (typeof s.call_at !== 'string' || !Number.isFinite(Date.parse(s.call_at))) return null;
  return s as CallStep;
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

/**
 * Reopen: the person's own judgement correcting the read, so the next pass places the card again. A refusal comes back as
 * an ApiError carrying the server's own words (404, 403, 409).
 */
export function reopenCallRead(store: string, judgementId: string): Promise<{ store: Store; judgementId: string; reopened: true }> {
  return fetch(`/api/triage/call-reads/${encodeURIComponent(judgementId)}/reopen`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ store }),
  }).then((r) => json<{ store: Store; judgementId: string; reopened: true }>(r));
}
